import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Clock, Info, Minus, Plus, Trash2, Users } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Select } from '@/shared/ui/Select';
import { useToast } from '@/shared/ui/Toast/useToast';
import patterns from '@/shared/ui/styles/patterns.module.css';
import type { SafetyRole } from '../api/safetyApi';
import {
  useSafetyContent,
  useSafetyRoster,
  useSetupMutation,
  useSkipSetupMutation,
  useSafetyLocationId,
} from '../hooks';
import { SafetyLoadError } from '../components/SafetyBits';
import styles from './SafetySetupPage.module.css';

interface StaffRow {
  name: string;
  roles: SafetyRole[];
  bbpExposed: boolean;
}

const ADMIN_ONLY_ROLES: SafetyRole[] = ['front_desk', 'office_manager'];
const isAdminOnlyRoles = (roles: SafetyRole[]): boolean =>
  roles.length > 0 && roles.every((r) => ADMIN_ONLY_ROLES.includes(r));

interface EquipmentRow {
  enabled: boolean;
  qty: number;
}

const MONTH_OPTIONS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
].map((label, i) => ({ value: String(i + 1), label }));

export function SafetySetupPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const locationId = useSafetyLocationId();
  const contentQuery = useSafetyContent();
  const { data: content } = contentQuery;
  const { data: roster } = useSafetyRoster();
  const setup = useSetupMutation();
  const skip = useSkipSetupMutation();

  const [staff, setStaff] = useState<StaffRow[]>([]);
  const [rosterApplied, setRosterApplied] = useState(false);
  const [trainingMonth, setTrainingMonth] = useState(9);
  const [equipment, setEquipment] = useState<Record<string, EquipmentRow>>({});

  // Prefill from active staff / team roster once, without clobbering edits.
  useEffect(() => {
    if (roster && !rosterApplied) {
      setStaff(
        roster.map((m) => {
          const roles = m.roles ?? ['assistant'];
          return { name: m.name, roles, bbpExposed: !isAdminOnlyRoles(roles) };
        }),
      );
      setRosterApplied(true);
    }
  }, [roster, rosterApplied]);

  // Pre-check the standard pack.
  useEffect(() => {
    if (content && Object.keys(equipment).length === 0) {
      const initial: Record<string, EquipmentRow> = {};
      for (const item of content.equipmentPack) {
        initial[item.packId] = { enabled: true, qty: item.defaultQty };
      }
      setEquipment(initial);
    }
  }, [content, equipment]);

  const roleOptions = useMemo(
    () => (content?.roles ?? []).map((r) => ({ value: r.value, label: r.label })),
    [content],
  );

  const updateStaff = (index: number, patch: Partial<StaffRow>) => {
    setStaff((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const validStaff = staff.filter((s) => s.name.trim().length > 0 && s.roles.length > 0);

  const handleGenerate = async () => {
    try {
      await setup.mutateAsync({
        locationId,
        trainingMonth,
        staff: validStaff.map((s) => ({
          name: s.name.trim(),
          roles: s.roles,
          bbpExposed: isAdminOnlyRoles(s.roles) ? s.bbpExposed : undefined,
        })),
        equipment: Object.entries(equipment)
          .filter(([, row]) => row.enabled)
          .map(([packId, row]) => ({ packId, qty: Math.max(1, row.qty) })),
      });
      toast.success('Safety & Training plan generated');
      navigate('/safety');
    } catch {
      toast.error('Could not generate the plan — check the form and try again');
    }
  };

  const handleSkip = async () => {
    try {
      await skip.mutateAsync();
      navigate('/safety');
    } catch {
      toast.error('Something went wrong — try again');
    }
  };

  if (contentQuery.isError) {
    return (
      <div className={styles.setup}>
        <SafetyLoadError onRetry={() => contentQuery.refetch()} />
      </div>
    );
  }

  return (
    <div className={styles.setup}>
      <div className={styles.header}>
        <h1 className={styles.title}>Let’s set up Safety &amp; Training</h1>
        <p className={styles.intro}>
          We’ve drafted a starter plan for a <strong>general dental practice in California</strong>,
          using your team roster. Review it, remove anything that doesn’t apply, and you’re done.
          Anything you skip, we’ll set up the first time it comes due — nothing blocks you from
          starting today.
        </p>
      </div>

      {/* Step 1 — team */}
      <section className={styles.step}>
        <div className={styles.stepHeader}>
          <span className={styles.stepNum}>1</span>
          <h2 className={styles.stepTitle}>Your team</h2>
        </div>
        <div className={styles.note}>
          <Users size={14} aria-hidden />
          <span>
            Pulled from <strong>Settings → Team & Staff</strong>. Confirm each role — the required
            training attaches automatically, so there’s nothing to pick per person.
          </span>
        </div>
        <div className={patterns.panel}>
          <div className={styles.personList}>
            {staff.map((person, i) => (
              <div key={i} className={styles.personRow}>
                <div className={styles.personRowTop}>
                  <Input
                    value={person.name}
                    onChange={(e) => updateStaff(i, { name: e.target.value })}
                    placeholder="Name"
                    aria-label="Team member name"
                    fullWidth
                  />
                  <button
                    className={styles.removeBtn}
                    onClick={() => setStaff((rows) => rows.filter((_, idx) => idx !== i))}
                    aria-label={`Remove ${person.name || 'row'}`}
                  >
                    <Trash2 size={14} aria-hidden />
                  </button>
                </div>
                <div className={styles.personRoleRow}>
                  {roleOptions.map((opt) => (
                    <label key={opt.value} className={styles.roleCheckbox}>
                      <input
                        type="checkbox"
                        checked={person.roles.includes(opt.value as SafetyRole)}
                        onChange={() =>
                          updateStaff(i, {
                            roles: person.roles.includes(opt.value as SafetyRole)
                              ? person.roles.filter((r) => r !== opt.value)
                              : [...person.roles, opt.value as SafetyRole],
                          })
                        }
                      />
                      {opt.label}
                    </label>
                  ))}
                  {isAdminOnlyRoles(person.roles) ? (
                    <label className={styles.bbpToggle}>
                      <input
                        type="checkbox"
                        checked={person.bbpExposed}
                        onChange={(e) => updateStaff(i, { bbpExposed: e.target.checked })}
                      />
                      Handles contaminated items (adds BBP)
                    </label>
                  ) : (
                    <span className={styles.autoAssign}>→ annual training</span>
                  )}
                </div>
              </div>
            ))}
            {staff.length === 0 && (
              <p className={styles.emptyHint}>No team members yet — add your first below.</p>
            )}
          </div>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Plus size={14} aria-hidden />}
            onClick={() =>
              setStaff((rows) => [...rows, { name: '', roles: ['assistant'], bbpExposed: true }])
            }
          >
            Add someone
          </Button>
        </div>
      </section>

      {/* Step 2 — training month */}
      <section className={styles.step}>
        <div className={styles.stepHeader}>
          <span className={styles.stepNum}>2</span>
          <h2 className={styles.stepTitle}>Training month</h2>
        </div>
        <div className={styles.note}>
          <Info size={14} aria-hidden />
          <span>
            One practice-wide date: everyone’s annual training lands in the same month, so there’s a
            single day to plan around. New hires train at hire, then join the cycle.
          </span>
        </div>
        <div className={patterns.panel}>
          <div className={styles.monthRow}>
            <Select
              label="Annual training month"
              value={String(trainingMonth)}
              onChange={(e) => setTrainingMonth(Number(e.target.value))}
              options={MONTH_OPTIONS}
            />
          </div>
        </div>
      </section>

      {/* Step 3 — equipment */}
      <section className={styles.step}>
        <div className={styles.stepHeader}>
          <span className={styles.stepNum}>3</span>
          <h2 className={styles.stepTitle}>Your equipment</h2>
        </div>
        <div className={styles.note}>
          <Info size={14} aria-hidden />
          <span>
            The standard set for a CA general practice — pre-checked for you.{' '}
            <strong>Uncheck what you don’t have</strong> and set how many. Cadence is set by
            regulation, so there’s nothing to configure.
          </span>
        </div>
        <div className={patterns.panel}>
          <div className={styles.equipList}>
            {(content?.equipmentPack ?? []).map((item) => {
              const row = equipment[item.packId] ?? { enabled: true, qty: item.defaultQty };
              return (
                <div
                  key={item.packId}
                  className={row.enabled ? styles.equipRow : styles.equipRowOff}
                >
                  <input
                    type="checkbox"
                    checked={row.enabled}
                    onChange={(e) =>
                      setEquipment((m) => ({
                        ...m,
                        [item.packId]: { ...row, enabled: e.target.checked },
                      }))
                    }
                    aria-label={item.name}
                  />
                  <div className={styles.equipName}>
                    {item.name}
                    {item.note && <small>{item.note}</small>}
                  </div>
                  <span className={styles.equipCadence}>{item.cadenceLabel}</span>
                  {item.qtyAdjustable ? (
                    <div className={styles.stepper}>
                      <button
                        onClick={() =>
                          setEquipment((m) => ({
                            ...m,
                            [item.packId]: { ...row, qty: Math.max(1, row.qty - 1) },
                          }))
                        }
                        disabled={!row.enabled}
                        aria-label={`Fewer ${item.name}`}
                      >
                        <Minus size={12} aria-hidden />
                      </button>
                      <span>{row.qty}</span>
                      <button
                        onClick={() =>
                          setEquipment((m) => ({
                            ...m,
                            [item.packId]: { ...row, qty: Math.min(20, row.qty + 1) },
                          }))
                        }
                        disabled={!row.enabled}
                        aria-label={`More ${item.name}`}
                      >
                        <Plus size={12} aria-hidden />
                      </button>
                    </div>
                  ) : (
                    <span className={styles.equipQtyNote}>×1</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <footer className={styles.footer}>
        <span className={styles.footerNote}>
          <Clock size={13} aria-hidden /> About 2 minutes · you can change anything later.
        </span>
        <div className={styles.footerActions}>
          <Button variant="secondary" onClick={handleSkip} loading={skip.isPending}>
            Skip — set me up as I go
          </Button>
          <Button
            variant="primary"
            leftIcon={<Check size={14} aria-hidden />}
            onClick={handleGenerate}
            loading={setup.isPending}
            disabled={validStaff.length === 0}
          >
            Generate plan &amp; open dashboard
          </Button>
        </div>
      </footer>
    </div>
  );
}
